import api from "../services/api";
import { useEffect, useState, useRef } from "react";
import { useSEO, SEO_PAGES } from "../hooks/useSEO";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import PhoneInput, { formatPhone } from "../components/PhoneInput";

/* ─────────────────────────────────────────────────
   COLOUR PALETTE  (Ayurveda pastels + deep accents)
───────────────────────────────────────────────── */
const C = {
  saffron:  "#E8922A",  saffronL: "#FDF0D5",  saffronD: "#B86A10",
  sage:     "#4A7C59",  sageL:    "#EAF4EE",   sageD:    "#2D5A3D",
  terra:    "#B85C38",
  cream:    "#FDFAF3",  creamD:   "#F5EDD8",
  turmeric: "#F7C948",
  copper:   "#8B4513",
  ink:      "#2C1810",  inkMid:   "#5C3D2E",   inkLight: "#9A7B60",
};

/* ─────────────────────────────────────────────────
   SVG ILLUSTRATIONS  (inline — no image files needed)
───────────────────────────────────────────────── */
const Lotus = ({ size = 56, color = C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i > 3 ? 0.45 : i > 1 ? 0.7 : 0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
    <ellipse cx="40" cy="66" rx="14" ry="5" fill={C.sage} opacity="0.3"/>
    <rect x="38" y="58" width="4" height="10" rx="2" fill={C.sage} opacity="0.55"/>
  </svg>
);

const Leaf = ({ size = 44, color = C.sage, rot = 0 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none"
    style={{ transform:`rotate(${rot}deg)` }}>
    <path d="M30 6 C52 6 58 30 30 54 C2 30 8 6 30 6Z" fill={color} opacity="0.85"/>
    <path d="M30 6 L30 54" stroke="white" strokeWidth="1.4" opacity="0.5"/>
    <path d="M30 18 C38 22 42 32 39 42" stroke="white" strokeWidth="0.9" opacity="0.45"/>
    <path d="M30 18 C22 22 18 32 21 42" stroke="white" strokeWidth="0.9" opacity="0.45"/>
  </svg>
);

const HerbsBowl = ({ size = 200 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" fill="none">
    {/* Bowl shadow */}
    <ellipse cx="100" cy="162" rx="64" ry="14" fill={C.copper} opacity="0.12"/>
    {/* Bowl body */}
    <path d="M36 110 Q100 155 164 110" fill={C.terra} opacity="0.6"/>
    <path d="M40 107 Q100 100 160 107 Q100 152 40 107Z" fill={C.saffronL}/>
    <ellipse cx="100" cy="107" rx="60" ry="15" fill={C.creamD}/>
    {/* Herbs in bowl */}
    <circle cx="82"  cy="102" r="8"  fill={C.sage}   opacity="0.85"/>
    <circle cx="100" cy="97"  r="10" fill={C.sageD}  opacity="0.8"/>
    <circle cx="118" cy="102" r="8"  fill={C.sage}   opacity="0.85"/>
    <circle cx="90"  cy="95"  r="6"  fill={C.turmeric} opacity="0.9"/>
    <circle cx="111" cy="94"  r="5"  fill={C.saffron}  opacity="0.85"/>
    {/* Stems */}
    <path d="M100 97 L94 70 Q90 60 84 54" stroke={C.sage} strokeWidth="2.2" fill="none"/>
    <path d="M100 97 L108 65 Q112 56 116 50" stroke={C.sageD} strokeWidth="2.2" fill="none"/>
    <path d="M100 97 L100 58 Q99 50 96 44" stroke={C.sage} strokeWidth="1.8" fill="none"/>
    {/* Leaves on stems */}
    <ellipse cx="84" cy="54" rx="9" ry="5" fill={C.sage} opacity="0.9" transform="rotate(-30 84 54)"/>
    <ellipse cx="116" cy="50" rx="9" ry="5" fill={C.sageD} opacity="0.9" transform="rotate(28 116 50)"/>
    <ellipse cx="96"  cy="44" rx="8" ry="4" fill={C.sage} opacity="0.9" transform="rotate(-8 96 44)"/>
    {/* Mandala ring around bowl */}
    {[0,45,90,135,180,225,270,315].map((d,i) => (
      <ellipse key={i} cx="100" cy="107" rx="3" ry="7"
        fill={C.saffron} opacity="0.2"
        transform={`rotate(${d} 100 107) translate(0 -72)`}/>
    ))}
  </svg>
);

/* Service / treatment icons */
const IconConsult   = () => <TxIcon bg={C.sageL}   border={C.sage}>
  <path d="M20 24h44v34H20z" rx="6" fill="none" stroke={C.sage} strokeWidth="2"/>
  <circle cx="36" cy="38" r="8" fill={C.creamD} stroke={C.sage} strokeWidth="1.5"/>
  <rect x="48" y="32" width="10" height="2.5" rx="1.2" fill={C.inkLight}/>
  <rect x="48" y="38" width="8"  height="2.5" rx="1.2" fill={C.inkLight}/>
  <rect x="48" y="44" width="9"  height="2.5" rx="1.2" fill={C.inkLight}/>
</TxIcon>;

const IconPancha    = () => <TxIcon bg={C.saffronL} border={C.saffron}>
  <circle cx="42" cy="42" r="20" fill="none" stroke={C.saffron} strokeWidth="1.5"/>
  {[0,60,120,180,240,300].map((d,i)=>(
    <ellipse key={i} cx="42" cy="42" rx="5" ry="12" fill={C.saffron}
      opacity="0.7" transform={`rotate(${d} 42 42) translate(0 -10)`}/>
  ))}
  <circle cx="42" cy="42" r="7" fill={C.turmeric} opacity="0.7"/>
</TxIcon>;

const IconShiro     = () => <TxIcon bg="#F0F4FF"    border="#9BB5E8">
  <ellipse cx="42" cy="36" rx="16" ry="18" fill={C.creamD} stroke={C.copper} strokeWidth="1.5"/>
  {[0,1,2,3,4].map(i=>(
    <path key={i} d={`M${36+i*2.5} 54 Q${36+i*2.5} 64 ${37+i*2.5} 70`}
      stroke={C.saffron} strokeWidth="1.4" fill="none" opacity="0.65"/>
  ))}
  <ellipse cx="42" cy="54" rx="9" ry="3" fill={C.saffron} opacity="0.3"/>
</TxIcon>;

const IconDiet      = () => <TxIcon bg="#F0FFF4"    border={C.sage}>
  <circle cx="42" cy="45" r="20" fill={C.sageL} stroke={C.sage} strokeWidth="1.5"/>
  <ellipse cx="42" cy="45" rx="13" ry="8" fill={C.creamD}/>
  <circle cx="36" cy="41" r="4"  fill={C.terra}    opacity="0.7"/>
  <circle cx="44" cy="39" r="5"  fill={C.turmeric} opacity="0.8"/>
  <circle cx="51" cy="43" r="3.5" fill={C.sage}   opacity="0.8"/>
  <rect x="40" y="24" width="4" height="9" rx="2" fill={C.sage}/>
</TxIcon>;

const IconYoga      = () => <TxIcon bg="#FFF5F0"    border={C.terra}>
  <circle cx="42" cy="20" r="6"  fill={C.terra}  opacity="0.85"/>
  <path d="M42 26 L42 48" stroke={C.copper} strokeWidth="2.8" strokeLinecap="round"/>
  <path d="M42 36 Q30 30 22 38" stroke={C.copper} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
  <path d="M42 36 Q54 30 62 38" stroke={C.copper} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
  <path d="M42 48 Q32 52 26 62" stroke={C.copper} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
  <path d="M42 48 Q52 52 58 62" stroke={C.copper} strokeWidth="2.2" strokeLinecap="round" fill="none"/>
</TxIcon>;

const IconDetox     = () => <TxIcon bg={C.sageL}   border={C.sage}>
  <path d="M24 58 Q24 72 42 72 Q60 72 60 58 L56 28 Q52 20 42 20 Q32 20 28 28Z"
    fill="none" stroke={C.sage} strokeWidth="1.5"/>
  <path d="M30 42 Q42 36 54 42" stroke={C.sage} strokeWidth="1.4" fill="none"/>
  <path d="M28 52 Q42 46 56 52" stroke={C.sage} strokeWidth="1.4" fill="none"/>
  {[32,40,48].map(x=><circle key={x} cx={x} cy="30" r="2.8" fill={C.turmeric} opacity="0.85"/>)}
</TxIcon>;

function TxIcon({ bg, border, children }) {
  return (
    <div style={{ width:56, height:56, borderRadius:14,
      background:bg, border:`1.5px solid ${border}40`,
      display:"flex", alignItems:"center", justifyContent:"center" }}>
      <svg width="64" height="64" viewBox="0 0 84 84" fill="none">{children}</svg>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   HERO BACKGROUND  (pure CSS — no images needed)
───────────────────────────────────────────────── */
function HeroBG() {
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", zIndex:0 }}>
      {/* Warm base gradient */}
      <div style={{ position:"absolute", inset:0,
        background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 45%,${C.saffronL} 80%,#FDE8C8 100%)` }}/>
      {/* Blurred colour blobs */}
      {[
        { w:520,h:520, top:"-18%",  left:"-12%",  col:C.saffron, op:0.09 },
        { w:380,h:380, top:"22%",   right:"-8%",  col:C.sage,    op:0.08 },
        { w:280,h:280, bottom:"-6%",left:"32%",   col:C.terra,   op:0.07 },
        { w:200,h:200, top:"12%",   left:"42%",   col:C.turmeric,op:0.11 },
      ].map((b,i)=>(
        <div key={i} style={{ position:"absolute", width:b.w, height:b.h,
          borderRadius:"50%", background:b.col, opacity:b.op,
          top:b.top, left:b.left, right:b.right, bottom:b.bottom,
          filter:"blur(72px)" }}/>
      ))}
      {/* Mandala SVG — top right */}
      <svg style={{ position:"absolute", right:"3%", top:"6%", opacity:0.055 }}
        width="440" height="440" viewBox="0 0 440 440">
        {[0,30,60,90,120,150,180,210,240,270,300,330].map((d,i)=>(
          <g key={i} transform={`rotate(${d} 220 220)`}>
            <ellipse cx="220" cy="76" rx="18" ry="44" fill={C.saffron}/>
          </g>
        ))}
        {[0,45,90,135,180,225,270,315].map((d,i)=>(
          <g key={i} transform={`rotate(${d} 220 220)`}>
            <ellipse cx="220" cy="134" rx="11" ry="24" fill={C.sage}/>
          </g>
        ))}
        <circle cx="220" cy="220" r="70" fill="none" stroke={C.saffron} strokeWidth="2"/>
        <circle cx="220" cy="220" r="44" fill="none" stroke={C.terra}   strokeWidth="1.5"/>
        <circle cx="220" cy="220" r="22" fill={C.turmeric} opacity="0.35"/>
      </svg>
      {/* Scattered leaves */}
      {[
        {top:"10%",  left:"5%",   rot:30,  op:0.15},
        {top:"68%",  left:"2%",   rot:-20, op:0.12},
        {top:"42%",  right:"4%",  rot:50,  op:0.13},
        {bottom:"8%",right:"10%", rot:-35, op:0.10},
      ].map((l,i)=>(
        <div key={i} style={{ position:"absolute", ...l,
          transform:`rotate(${l.rot}deg)`, opacity:l.op, pointerEvents:"none" }}>
          <Leaf size={58} color={C.sage}/>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   GRADIENT BUTTON
───────────────────────────────────────────────── */
function Btn({ href, to, children, outline=false, small=false, style:sx={} }) {
  const base = {
    display:"inline-flex", alignItems:"center", gap:8, cursor:"pointer",
    padding: small ? "9px 22px" : "13px 32px",
    borderRadius:50, fontSize: small ? 13 : 15, fontWeight:600,
    textDecoration:"none", fontFamily:"'Inter',sans-serif",
    border: outline ? `2px solid ${C.saffron}` : "none",
    background: outline
      ? "transparent"
      : `linear-gradient(135deg,${C.saffron} 0%,${C.terra} 100%)`,
    color: outline ? C.saffron : "#fff",
    boxShadow: outline ? "none" : `0 8px 26px rgba(232,146,42,0.38)`,
    transition:"all 0.25s",
    ...sx,
  };
  const target = href || to || "/";
  return (
    <motion.a href={target}
      whileHover={{ scale:1.04,
        boxShadow: outline ? "none" : `0 14px 34px rgba(232,146,42,0.50)` }}
      whileTap={{ scale:0.97 }}
      style={base}>
      {children}
    </motion.a>
  );
}

/* ─────────────────────────────────────────────────
   SECTION HEADING
───────────────────────────────────────────────── */
function Head({ tag, title, sub }) {
  return (
    <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
      viewport={{ once:true }} transition={{ duration:0.5 }}
      style={{ textAlign:"center", marginBottom:48 }}>
      {tag && (
        <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:14,
          background:C.saffronL, border:`1px solid ${C.saffron}40`,
          borderRadius:20, padding:"4px 16px" }}>
          <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
            color:C.saffronD, textTransform:"uppercase",
            fontFamily:"'Inter',sans-serif" }}>{tag}</span>
        </div>
      )}
      <h2 style={{ fontFamily:"'Playfair Display',serif",
        fontSize:"clamp(26px,4vw,42px)", fontWeight:700,
        color:C.ink, margin:"0 0 14px", lineHeight:1.2 }}>
        {title}
      </h2>
      {sub && (
        <p style={{ fontSize:16, color:C.inkLight, maxWidth:580,
          margin:"0 auto", lineHeight:1.75,
          fontFamily:"'Merriweather',serif" }}>{sub}</p>
      )}
      <div style={{ width:60, height:3, borderRadius:2, margin:"20px auto 0",
        background:`linear-gradient(90deg,${C.saffron},${C.terra})` }}/>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────
   HORIZONTAL SCROLL TREATMENTS
───────────────────────────────────────────────── */
function TreatmentScroll({ items }) {
  const ref = useRef(null);
  const scroll = d => ref.current?.scrollBy({ left: d*280, behavior:"smooth" });
  return (
    <div style={{ position:"relative" }}>
      {[-1,1].map((d,i) => (
        <button key={i} onClick={() => scroll(d)} style={{
          position:"absolute", top:"50%",
          [i===0?"left":"right"]: -20,
          transform:"translateY(-50%)", zIndex:5,
          width:44, height:44, borderRadius:"50%",
          background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
          border:"none", color:"#fff", fontSize:22, cursor:"pointer",
          boxShadow:`0 4px 16px rgba(200,100,40,0.38)`,
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          {i===0?"‹":"›"}
        </button>
      ))}
      <div ref={ref} style={{ display:"flex", gap:20, overflowX:"auto",
        paddingBottom:8, scrollbarWidth:"none", WebkitOverflowScrolling:"touch" }}>
        <style>{`#tx-track::-webkit-scrollbar{display:none}`}</style>
        {items.map((t,i) => (
          <motion.div key={i}
            initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} transition={{ delay:i*0.07 }}
            whileHover={{ y:-7, boxShadow:"0 22px 44px rgba(0,0,0,0.11)" }}
            style={{ minWidth:238, background:"#fff", borderRadius:22,
              padding:"28px 22px", flexShrink:0,
              boxShadow:"0 4px 20px rgba(0,0,0,0.065)",
              border:`1px solid ${C.saffronL}` }}>
            <div style={{ marginBottom:14 }}>{t.icon}</div>
            <div style={{ fontFamily:"'Playfair Display',serif",
              fontSize:16, fontWeight:700, color:C.ink, marginBottom:8 }}>
              {t.title}
            </div>
            <div style={{ fontSize:13, color:C.inkLight, lineHeight:1.65,
              fontFamily:"'Merriweather',serif" }}>
              {t.desc}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   ANIMATED COUNTER
───────────────────────────────────────────────── */
function CountUp({ to, suffix="" }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || fired.current) return;
      fired.current = true;
      const target = parseInt(to) || 0;
      const step = Math.ceil(target / 55);
      let cur = 0;
      const t = setInterval(() => {
        cur = Math.min(cur + step, target);
        setN(cur);
        if (cur >= target) clearInterval(t);
      }, 22);
    }, { threshold:0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{n}{suffix}</span>;
}

/* ─────────────────────────────────────────────────
   TESTIMONIALS CAROUSEL
───────────────────────────────────────────────── */
const DEFAULT_TESTIMONIALS = [
  { name:"Meera Sharma",   loc:"Pune",      text:"After years of chronic fatigue, Dr. Patil's Ayurvedic treatment truly transformed my life. I feel balanced and full of energy again." },
  { name:"Rajesh Nair",    loc:"Mumbai",    text:"The Pañcakarma therapy here is exceptional. Everything is done with care and authenticity. I highly recommend this clinic." },
  { name:"Anita Kulkarni", loc:"Nashik",    text:"The dietary guidance and herbal medicines worked wonders for my digestion. A truly holistic, life-changing experience." },
  { name:"Suresh Patel",   loc:"Ahmedabad", text:"The Shirodhara session was the most calming experience of my life. The staff is warm, knowledgeable, and genuinely dedicated." },
];

function Testimonials({ list }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(x => (x+1) % list.length), 4800);
    return () => clearInterval(t);
  }, [list.length]);
  const t = list[i];
  return (
    <div style={{ maxWidth:680, margin:"0 auto", textAlign:"center" }}>
      <AnimatePresence mode="wait">
        <motion.div key={i}
          initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
          exit={{ opacity:0, y:-14 }} transition={{ duration:0.4 }}>
          <div style={{ fontSize:80, lineHeight:0.8, color:C.saffron,
            opacity:0.28, fontFamily:"serif" }}>"</div>
          <p style={{ fontSize:17, color:C.inkMid, lineHeight:1.85,
            fontFamily:"'Merriweather',serif", fontStyle:"italic",
            margin:"12px 16px 24px" }}>
            {t.text}
          </p>
          <div style={{ display:"flex", alignItems:"center",
            justifyContent:"center", gap:12 }}>
            <div style={{ width:46, height:46, borderRadius:"50%",
              background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
              display:"flex", alignItems:"center", justifyContent:"center",
              color:"#fff", fontWeight:700, fontSize:20,
              fontFamily:"'Playfair Display',serif" }}>
              {t.name[0]}
            </div>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontWeight:700, color:C.ink, fontSize:15,
                fontFamily:"'Playfair Display',serif" }}>{t.name}</div>
              <div style={{ fontSize:12, color:C.inkLight,
                fontFamily:"'Inter',sans-serif" }}>{t.loc}</div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      {/* Dot navigation */}
      <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:26 }}>
        {list.map((_,j) => (
          <button key={j} onClick={() => setI(j)} style={{
            width: j===i ? 26 : 8, height:8, borderRadius:4, padding:0,
            background: j===i ? C.saffron : C.creamD,
            border:"none", cursor:"pointer", transition:"all 0.3s",
          }}/>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   FLOATING WHATSAPP
───────────────────────────────────────────────── */
function WAFloat({ number }) {
  if (!number) return null;
  const n = number.replace(/\D/g,"");
  return (
    <motion.a href={`https://wa.me/91${n}`} target="_blank" rel="noopener noreferrer"
      initial={{ scale:0 }} animate={{ scale:1 }}
      transition={{ delay:1.8, type:"spring", stiffness:200 }}
      whileHover={{ scale:1.12 }} whileTap={{ scale:0.93 }}
      style={{ position:"fixed", bottom:28, right:28, zIndex:8000,
        width:62, height:62, borderRadius:"50%",
        background:"linear-gradient(135deg,#25D366,#128C7E)",
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow:"0 8px 28px rgba(37,211,102,0.45)",
        textDecoration:"none" }}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
      <motion.div animate={{ scale:[1,1.55,1], opacity:[0.5,0,0.5] }}
        transition={{ duration:2.2, repeat:Infinity }}
        style={{ position:"absolute", inset:0, borderRadius:"50%",
          border:"2px solid #25D366", pointerEvents:"none" }}/>
    </motion.a>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════════════════════════ */
export default function Home() {
  useSEO(SEO_PAGES.home);
  const [cms, setCms]       = useState({});
  const [phone, setPhone]   = useState({ countryCode: "+91", number: "" });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [dailyPosts,   setDailyPosts]   = useState([]);
  const [seasonalRegimen, setSeasonalRegimen] = useState(null);
  const [flippedId,    setFlippedId]    = useState(null);  // which card is flipped
  const [lightboxPost, setLightboxPost] = useState(null);  // image lightbox
  const [clinicMode,   setClinicMode]   = useState("online_only"); // "physical" | "online_only"

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef, offset: ["start start","end start"]
  });
  const parallaxY  = useTransform(scrollYProgress, [0,1], ["0%",  "28%"]);
  const parallaxOp = useTransform(scrollYProgress, [0,0.75], [1, 0]);

  useEffect(() => {
    api.get("/content/home")
      .then(r => setCms(r.data?.data || {}))
      .catch(() => {});
    api.get("/daily-posts")
      .then(r => setDailyPosts(r.data || []))
      .catch(() => {});
    api.get("/seasonal-regimen")
      .then(r => setSeasonalRegimen(r.data || null))
      .catch(() => {});
    api.get("/settings/clinicMode")
      .then(r => { if (r.data === "physical" || r.data === "online_only") setClinicMode(r.data); })
      .catch(() => {});
  }, []);

  const checkStatus = async () => {
    if (!phone.number.trim()) return;
    setLoading(true); setSearched(true);
    try {
      const r = await api.get(`/requests/status/${encodeURIComponent(formatPhone(phone))}`);
      setResults(r.data || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  /* ── CMS with fallbacks ── */
  const heroTitle    = cms.heroTitle    || "Heramba Ayurveda,\nTimeless Healing";
  const heroSub      = cms.heroSubtitle || "Rooted in 5,000 years of Vedic wisdom —\ndelivered with warmth and modern care.";
  const ctaText      = cms.ctaText      || "Book Appointment";
  const aboutSummary = cms.aboutSummary || "At our clinic we practise pure, classical Ayurveda. Every treatment is rooted in ancient Samhita texts, personalised to your Prakriti, and delivered with wholehearted care by experienced physicians.";
  const trustText    = cms.trustText    || "Trusted by over a thousand patients across Maharashtra for safe, authentic, and holistic Ayurvedic healing.";
  const phoneNo      = cms.phone     || "";
  const whatsappNo   = cms.whatsapp  || phoneNo;

  const treatments = [
    { icon:<IconConsult/>,  title:cms.t1title||"Ayurvedic Consultation", desc:cms.t1desc||"Classical nadi pariksha and dosha assessment for personalised healing plans." },
    { icon:<IconPancha/>,   title:cms.t2title||"Pañcakarma Therapy",     desc:cms.t2desc||"Complete five-fold detoxification and deep-tissue rejuvenation of body and mind." },
    { icon:<IconShiro/>,    title:cms.t3title||"Shirodhara",             desc:cms.t3desc||"Warm medicated oil stream on the forehead for profound mental calm and clarity." },
    { icon:<IconDiet/>,     title:cms.t4title||"Diet & Lifestyle",       desc:cms.t4desc||"Prakṛti-based daily nutrition and seasonal routine for sustained wellbeing." },
    { icon:<IconYoga/>,     title:cms.t5title||"Yoga & Pranayama",       desc:cms.t5desc||"Breath-led movement practices tailored precisely to your constitution." },
    { icon:<IconDetox/>,    title:cms.t6title||"Herbal Medicines",       desc:cms.t6desc||"Authentic classical formulations prepared fresh — zero synthetic additives." },
  ];

  const stats = [
    { n: parseInt(cms.statPatients)   || 1200, s:"+", label:"Happy Patients"     },
    { n: parseInt(cms.statYears)      || 12,   s:"+", label:"Years of Practice"  },
    { n: parseInt(cms.statTreatments) || 25,   s:"+", label:"Treatments Offered" },
    { n: parseInt(cms.statDoctors)    || 4,    s:"",  label:"Expert Doctors"     },
  ];

  /* ── CMS-driven testimonials ── */
  const buildTestis = () => {
    const fromCms = [1,2,3,4].map(n => ({
      name: cms[`testi${n}Name`],
      loc:  cms[`testi${n}Loc`],
      text: cms[`testi${n}Text`],
    })).filter(t => t.name && t.text);
    return fromCms.length ? fromCms : DEFAULT_TESTIMONIALS;
  };
  const testimonials = buildTestis();

  /* ══════ RENDER ══════ */
  return (
    <div style={{ background:C.cream, minHeight:"100vh",
      paddingTop:72, overflowX:"hidden" }}>

      {/* ════════════════════════════════════════
          §1  HERO  — parallax
          ════════════════════════════════════════ */}
      <section ref={heroRef} style={{ position:"relative",
        minHeight:"92vh", display:"flex", alignItems:"center",
        justifyContent:"center", overflow:"hidden" }}>
        <HeroBG/>

        <motion.div style={{ y:parallaxY, opacity:parallaxOp,
          position:"relative", zIndex:2, textAlign:"center",
          padding:"clamp(40px,8vw,64px) 20px clamp(60px,10vw,88px)", maxWidth:840, margin:"0 auto" }}>

          {/* Sanskrit line */}
          <motion.p initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:0.2 }}
            style={{ fontSize:12, letterSpacing:"0.18em", color:C.saffronD,
              fontFamily:"'Merriweather',serif", fontStyle:"italic",
              marginBottom:20, opacity:0.75 }}>
            ॐ सर्वे भवन्तु सुखिनः &nbsp;·&nbsp; May all beings be well
          </motion.p>

          {/* Main headline — English */}
          <motion.h1 initial={{ opacity:0, y:32 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:0.62, delay:0.08 }}
            style={{ fontFamily:"'Playfair Display',serif",
              fontSize:"clamp(36px,6.5vw,76px)", fontWeight:800,
              color:C.ink, lineHeight:1.13, marginBottom:10,
              whiteSpace:"pre-line" }}>
            {heroTitle}
          </motion.h1>

          {/* Devanagari headline — Amita Bold — prominent, not neglected */}
          <motion.div
            initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:0.18, duration:0.65 }}
            style={{ marginBottom:24, position:"relative" }}>
            {/* Decorative side lines */}
            <div style={{
              display:"flex", alignItems:"center",
              justifyContent:"center", gap:16,
            }}>
              <div style={{ height:1.5, flex:1, maxWidth:80,
                background:`linear-gradient(90deg,transparent,${C.saffron}80)` }}/>
              <div style={{ position:"relative" }}>
                {/* Glow behind text */}
                <div style={{
                  position:"absolute", inset:"-8px -16px",
                  background:`radial-gradient(ellipse,${C.saffron}18 0%,transparent 70%)`,
                  borderRadius:40, pointerEvents:"none",
                }}/>
                <span style={{
                  fontFamily:"'Amita', serif",
                  fontWeight:700,
                  fontSize:"clamp(30px,5vw,58px)",
                  background:`linear-gradient(135deg, ${C.saffron} 0%, ${C.terra} 45%, ${C.copper} 100%)`,
                  WebkitBackgroundClip:"text",
                  WebkitTextFillColor:"transparent",
                  backgroundClip:"text",
                  letterSpacing:"0.06em",
                  lineHeight:1.5,
                  display:"inline-block",
                  position:"relative",
                }}>
                  हेरम्ब आयुर्वेद
                </span>
              </div>
              <div style={{ height:1.5, flex:1, maxWidth:80,
                background:`linear-gradient(90deg,${C.saffron}80,transparent)` }}/>
            </div>
            {/* Underline accent */}
            <motion.div
              initial={{ scaleX:0 }} animate={{ scaleX:1 }}
              transition={{ delay:0.45, duration:0.6 }}
              style={{
                height:2, width:"clamp(120px,30%,220px)",
                margin:"6px auto 0",
                background:`linear-gradient(90deg,transparent,${C.saffron},${C.terra},transparent)`,
                borderRadius:2,
              }}/>
          </motion.div>

          {/* Lotus divider */}
          <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }}
            transition={{ delay:0.38, duration:0.55 }}
            style={{ display:"flex", alignItems:"center",
              justifyContent:"center", gap:14, marginBottom:22 }}>
            <div style={{ height:1, width:64,
              background:`linear-gradient(90deg,transparent,${C.saffron})` }}/>
            <Lotus size={38} color={C.saffron}/>
            <div style={{ height:1, width:64,
              background:`linear-gradient(90deg,${C.saffron},transparent)` }}/>
          </motion.div>

          {/* Subtitle */}
          <motion.p initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:0.28 }}
            style={{ fontSize:"clamp(15px,2.2vw,20px)", color:C.inkMid,
              lineHeight:1.75, marginBottom:40,
              fontFamily:"'Merriweather',serif", whiteSpace:"pre-line" }}>
            {heroSub}
          </motion.p>

          {/* CTA buttons */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:0.42 }}
            style={{ display:"flex", gap:14, justifyContent:"center",
              flexWrap:"wrap" }}>
            <Btn to="/request">🌿 {ctaText}</Btn>
            <Btn to="/services" outline>View Treatments</Btn>
          </motion.div>

          {/* Trust badges */}
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
            transition={{ delay:0.58 }}
            style={{ display:"flex", gap:20, justifyContent:"center",
              flexWrap:"wrap", marginTop:44 }}>
            {[
              { e:"⭐", t:"4.9 / 5 Rating"  },
              { e:"🏥", t:"Est. 2012"        },
              { e:"🌿", t:"100 % Natural"    },
              { e:"🔬", t:"Evidence-Based"   },
            ].map((b,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:7,
                background:"rgba(255,255,255,0.62)", backdropFilter:"blur(10px)",
                borderRadius:30, padding:"7px 18px",
                border:"1px solid rgba(255,255,255,0.82)",
                boxShadow:"0 2px 12px rgba(0,0,0,0.055)" }}>
                <span style={{ fontSize:14 }}>{b.e}</span>
                <span style={{ fontSize:11.5, fontWeight:600, color:C.inkMid,
                  fontFamily:"'Inter',sans-serif" }}>{b.t}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div animate={{ y:[0,9,0] }} transition={{ duration:2, repeat:Infinity }}
          style={{ position:"absolute", bottom:28, left:"50%",
            transform:"translateX(-50%)", zIndex:3 }}>
          <div style={{ width:26, height:42, borderRadius:13,
            border:`2px solid ${C.saffron}80`,
            display:"flex", alignItems:"flex-start",
            justifyContent:"center", padding:"6px 0" }}>
            <motion.div animate={{ y:[0,13,0] }}
              transition={{ duration:2, repeat:Infinity }}
              style={{ width:4, height:8, borderRadius:2,
                background:C.saffron }}/>
          </div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════
          §8b  DAILY POSTS
          ════════════════════════════════════════ */}
      {dailyPosts.length > 0 && (
        <section style={{
          padding:"clamp(56px,8vw,100px) clamp(16px,4vw,32px)",
          background:`linear-gradient(180deg, #fff 0%, ${C.cream} 60%, ${C.creamD} 100%)`,
          position:"relative", overflow:"hidden",
        }}>
          {/* large ambient decoration */}
          <div style={{ position:"absolute", left:-120, top:-100, opacity:0.035, pointerEvents:"none", transform:"rotate(-20deg)" }}>
            <Lotus size={520} color={C.saffron}/>
          </div>
          <div style={{ position:"absolute", right:-80, bottom:-80, opacity:0.03, pointerEvents:"none" }}>
            <Lotus size={400} color={C.sage}/>
          </div>

          {/* ── Lightbox ── */}
          <AnimatePresence>
            {lightboxPost && (
              <motion.div
                initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                onClick={() => setLightboxPost(null)}
                style={{ position:"fixed", inset:0, background:"rgba(10,8,6,0.92)",
                  zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24,
                  backdropFilter:"blur(6px)" }}
              >
                <motion.div
                  initial={{ scale:0.88, opacity:0 }} animate={{ scale:1, opacity:1 }}
                  exit={{ scale:0.88, opacity:0 }}
                  transition={{ type:"spring", stiffness:280, damping:26 }}
                  style={{ position:"relative", maxWidth:"90vw" }}
                  onClick={e => e.stopPropagation()}
                >
                  <img
                    src={lightboxPost.imageUrl?.startsWith("http") ? lightboxPost.imageUrl : `${window.location.hostname === "localhost" ? "http://localhost:5000" : "https://herambaayurveda.in"}${lightboxPost.imageUrl}`}
                    alt={lightboxPost.title}
                    style={{ maxWidth:"100%", maxHeight:"82vh", borderRadius:20,
                      objectFit:"contain", boxShadow:"0 40px 120px rgba(0,0,0,0.7)",
                      display:"block" }}
                  />
                  <div style={{ marginTop:16, textAlign:"center" }}>
                    <p style={{ color:"rgba(255,255,255,0.8)", fontSize:14,
                      fontFamily:"'Playfair Display',serif", margin:0 }}>
                      {lightboxPost.title}
                    </p>
                  </div>
                </motion.div>
                <button onClick={() => setLightboxPost(null)}
                  style={{ position:"absolute", top:24, right:28,
                    background:"rgba(255,255,255,0.1)", backdropFilter:"blur(8px)",
                    border:"1px solid rgba(255,255,255,0.2)", borderRadius:"50%",
                    width:46, height:46, color:"#fff", fontSize:22, cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.2)"}
                  onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                >✕</button>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ maxWidth:1240, margin:"0 auto", position:"relative" }}>

            {/* Section heading */}
            <motion.div initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }}
              viewport={{ once:true }} style={{ textAlign:"center", marginBottom:64 }}>
              <Head tag="Health & Wellness" title="Daily Insights"
                sub="Our team shares Ayurvedic wisdom, seasonal health tips and clinic news every day." />
            </motion.div>

            {/* ── Cards — full-width stacked like "Available Worldwide" card ── */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(340px, 100%), 1fr))", gap:28 }}>
              {dailyPosts.map((post, i) => {
                const isExpanded = flippedId === post._id;
                const imgSrc = post.imageUrl
                  ? (post.imageUrl.startsWith("http") ? post.imageUrl : `${window.location.hostname === "localhost" ? "http://localhost:5000" : "https://herambaayurveda.in"}${post.imageUrl}`)
                  : null;

                return (
                  <motion.div key={post._id}
                    initial={{ opacity:0, y:40 }} whileInView={{ opacity:1, y:0 }}
                    viewport={{ once:true }} transition={{ delay: Math.min(i * 0.08, 0.4), duration:0.55 }}
                    style={{
                      borderRadius:24, overflow:"hidden",
                      background:"#fff",
                      boxShadow:"0 8px 48px rgba(44,24,16,0.11)",
                      border:`1px solid ${C.creamD}`,
                    }}
                  >
                    {/* ── IMAGE — full width on top, always visible ── */}
                    {imgSrc && (
                      <div
                        onClick={() => setLightboxPost(post)}
                        style={{
                          position:"relative", overflow:"hidden",
                          cursor:"zoom-in", height:260,
                          flexShrink:0,
                        }}
                      >
                        <img src={imgSrc} alt={post.title}
                          style={{
                            width:"100%", height:"100%",
                            objectFit:"cover", objectPosition:"center center",
                            transition:"transform 0.55s ease", display:"block",
                          }}
                          onMouseEnter={e => e.target.style.transform="scale(1.05)"}
                          onMouseLeave={e => e.target.style.transform="scale(1)"}
                        />
                        {/* gradient overlay at bottom */}
                        <div style={{
                          position:"absolute", inset:0,
                          background:"linear-gradient(to bottom, transparent 55%, rgba(20,12,4,0.45) 100%)",
                          pointerEvents:"none",
                        }}/>
                        {/* View badge */}
                        <div style={{
                          position:"absolute", top:14, right:14,
                          background:"rgba(0,0,0,0.38)", backdropFilter:"blur(6px)",
                          borderRadius:30, padding:"5px 13px",
                          fontSize:11, color:"#fff", fontFamily:"'Inter',sans-serif",
                          display:"flex", alignItems:"center", gap:4,
                          border:"1px solid rgba(255,255,255,0.2)",
                          pointerEvents:"none",
                        }}>🔍 View</div>
                        {/* Date on image */}
                        <div style={{
                          position:"absolute", bottom:12, left:16,
                          fontSize:11, color:"rgba(255,255,255,0.88)",
                          fontFamily:"'Inter',sans-serif", fontWeight:500,
                          textShadow:"0 1px 4px rgba(0,0,0,0.6)",
                          pointerEvents:"none",
                        }}>
                          {new Date(post.createdAt).toLocaleDateString("en-IN",
                            { day:"numeric", month:"long", year:"numeric" })}
                        </div>
                      </div>
                    )}

                    {/* ── CONTENT — always below image ── */}
                    <div style={{
                      padding:"clamp(18px,3vw,28px) clamp(18px,3.5vw,32px)",
                      display:"flex", flexDirection:"column", gap:12,
                    }}>
                      {/* Date badge when no image */}
                      {!imgSrc && (
                        <div style={{
                          display:"inline-flex", alignItems:"center", gap:7,
                          background:C.saffronL, borderRadius:20,
                          padding:"5px 14px", alignSelf:"flex-start",
                          border:`1px solid ${C.saffron}30`,
                        }}>
                          <span style={{ fontSize:12 }}>📅</span>
                          <span style={{ fontSize:11, fontWeight:600, color:C.saffronD,
                            fontFamily:"'Inter',sans-serif" }}>
                            {new Date(post.createdAt).toLocaleDateString("en-IN",
                              { day:"numeric", month:"long", year:"numeric" })}
                          </span>
                        </div>
                      )}

                      {/* Title */}
                      <h3 style={{
                        fontFamily:"'Playfair Display',serif",
                        fontSize:"clamp(18px,2.2vw,24px)", fontWeight:800, color:C.ink,
                        margin:0, lineHeight:1.3,
                      }}>
                        {post.title}
                      </h3>

                      {/* Summary */}
                      <p style={{
                        fontSize:"clamp(13px,1.1vw,15px)", color:C.inkMid, lineHeight:1.8,
                        fontFamily:"'Merriweather',serif", margin:0,
                        display: isExpanded ? "block" : "-webkit-box",
                        WebkitLineClamp: isExpanded ? "unset" : 3,
                        WebkitBoxOrient: "vertical",
                        overflow: isExpanded ? "visible" : "hidden",
                      }}>
                        {post.summary}
                      </p>

                      {/* Expanded full body */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity:0, height:0 }}
                            animate={{ opacity:1, height:"auto" }}
                            exit={{ opacity:0, height:0 }}
                            transition={{ duration:0.35 }}
                            style={{ overflow:"hidden" }}
                          >
                            <div style={{
                              background:`linear-gradient(135deg,${C.sageD} 0%,#1a3d2a 100%)`,
                              borderRadius:16, padding:"clamp(14px,2.5vw,22px) clamp(16px,3vw,24px)",
                              marginTop:4,
                            }}>
                              <p style={{
                                fontSize:"clamp(13px,1.1vw,15px)", color:"rgba(255,255,255,0.9)",
                                lineHeight:1.9, fontFamily:"'Merriweather',serif",
                                margin:0, whiteSpace:"pre-line",
                              }}>
                                {post.body}
                              </p>
                              <div style={{
                                display:"flex", alignItems:"center", gap:10,
                                marginTop:16, paddingTop:14,
                                borderTop:"1px solid rgba(255,255,255,0.12)",
                              }}>
                                <div style={{
                                  width:32, height:32, borderRadius:"50%", flexShrink:0,
                                  background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  fontSize:14,
                                }}>🌿</div>
                                <div>
                                  <p style={{ margin:0, fontSize:13, color:"rgba(255,255,255,0.92)",
                                    fontFamily:"'Inter',sans-serif", fontWeight:600 }}>{post.postedBy}</p>
                                  <p style={{ margin:0, fontSize:11, color:"rgba(255,255,255,0.45)",
                                    fontFamily:"'Inter',sans-serif" }}>
                                    {new Date(post.createdAt).toLocaleDateString("en-IN",
                                      { day:"numeric", month:"long", year:"numeric" })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Footer */}
                      <div style={{
                        display:"flex", alignItems:"center",
                        justifyContent:"space-between",
                        paddingTop:12,
                        borderTop:`1.5px solid ${C.creamD}`,
                        gap:10, marginTop:4,
                      }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                          <div style={{
                            width:30, height:30, borderRadius:"50%", flexShrink:0,
                            background:`linear-gradient(135deg,${C.sage},${C.sageD})`,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:14,
                          }}>🌿</div>
                          <span style={{ fontSize:13, color:C.inkLight,
                            fontFamily:"'Inter',sans-serif", fontWeight:500,
                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {post.postedBy}
                          </span>
                        </div>
                        <motion.button
                          whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
                          onClick={() => setFlippedId(isExpanded ? null : post._id)}
                          style={{
                            fontSize:13, fontWeight:700, color:"#fff",
                            background: isExpanded
                              ? `linear-gradient(135deg,${C.terra},${C.saffronD})`
                              : `linear-gradient(135deg,${C.sage},${C.sageD})`,
                            border:"none", cursor:"pointer",
                            fontFamily:"'Inter',sans-serif",
                            padding:"9px 20px", borderRadius:30, flexShrink:0,
                            boxShadow:`0 4px 16px rgba(74,124,89,0.32)`,
                            display:"flex", alignItems:"center", gap:6,
                            whiteSpace:"nowrap",
                          }}
                        >
                          {isExpanded ? "Show less ↑" : "Read more →"}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════
          §8c  SEASONAL REGIMEN CARD
          ════════════════════════════════════════ */}
      {seasonalRegimen?.isPublished !== false && (
      <section style={{
        padding:"clamp(0px,0vw,0px) clamp(16px,4vw,32px) clamp(36px,5vw,64px)",
        background:`linear-gradient(180deg, ${C.cream} 0%, #fff 60%, ${C.creamD} 100%)`,
        position:"relative", overflow:"hidden",
      }}>
        {/* Ambient decorations */}
        <div style={{ position:"absolute", right:-100, top:-60, opacity:0.03, pointerEvents:"none" }}>
          <Leaf size={480} color={C.sage}/>
        </div>
        <div style={{ position:"absolute", left:-60, bottom:-40, opacity:0.04, pointerEvents:"none", transform:"rotate(30deg)" }}>
          <Lotus size={300} color={C.saffron}/>
        </div>

        <div style={{ maxWidth:1240, margin:"0 auto", position:"relative" }}>
          <motion.div
            initial={{ opacity:0, y:28 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} transition={{ duration:0.6 }}
            style={{
              background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
              borderRadius:28, overflow:"hidden", position:"relative",
              boxShadow:"0 24px 64px rgba(0,0,0,0.22)",
            }}
          >
            {/* Background decorations */}
            <div style={{ position:"absolute", right:-60, top:-60, opacity:0.06, pointerEvents:"none" }}>
              <Lotus size={320} color="#fff"/>
            </div>
            <div style={{ position:"absolute", left:-30, bottom:-30, opacity:0.04, pointerEvents:"none" }}>
              <Lotus size={200} color={C.turmeric}/>
            </div>
            <div style={{ position:"absolute", left:20, top:20, opacity:0.07, pointerEvents:"none", transform:"rotate(-15deg)" }}>
              <Leaf size={140} color={C.sage}/>
            </div>

            <div style={{ position:"relative", zIndex:1 }}>

              {/* ── TOP: full, uncropped photo (no more cover-cropping — the
                   whole image always shows, since it may carry its own
                   visual info that shouldn't be cut off) ── */}
              <div style={{ width:"100%", background:"rgba(0,0,0,0.25)", display:"flex", justifyContent:"center" }}>
                <img
                  src={seasonalRegimen?.imageUrl || "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80"}
                  alt="Seasonal Regimen"
                  style={{
                    width:"100%", height:"auto",
                    objectFit:"contain", objectPosition:"center",
                    display:"block",
                  }}
                />
              </div>

              {/* ── Caption strip: season badge + name, below the photo now
                   instead of overlaid on top of it ── */}
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                flexWrap:"wrap", gap:10,
                padding:"14px 18px",
                background:"rgba(255,255,255,0.04)",
                borderBottom:"1px solid rgba(255,255,255,0.08)",
              }}>
                <div>
                  <p style={{ margin:0, fontSize:"clamp(18px,2.4vw,24px)",
                    fontFamily:"'Playfair Display',serif", fontWeight:800,
                    color:"#fff", lineHeight:1.2 }}>
                    {seasonalRegimen?.seasonName || "Vasanta Ṛtu"}
                  </p>
                  <p style={{ margin:"2px 0 0", fontSize:12.5,
                    color:"rgba(255,255,255,0.55)",
                    fontFamily:"'Merriweather',serif", fontStyle:"italic" }}>
                    {seasonalRegimen?.seasonPeriod || "Spring · Mar – May"}
                  </p>
                </div>
                <div style={{
                  display:"inline-flex", alignItems:"center", gap:8, flexShrink:0,
                  background:"rgba(247,201,72,0.14)",
                  borderRadius:20, padding:"6px 16px",
                  border:"1px solid rgba(247,201,72,0.4)",
                }}>
                  <span style={{ fontSize:14 }}>{seasonalRegimen?.seasonEmoji || "🌿"}</span>
                  <span style={{ fontSize:10.5, fontWeight:700, letterSpacing:"0.13em",
                    color:C.turmeric, textTransform:"uppercase",
                    fontFamily:"'Inter',sans-serif" }}>
                    {seasonalRegimen?.seasonLabel || "Current Season"}
                  </span>
                </div>
              </div>

              {/* ── BELOW: Content ── */}
              <div style={{ padding:"clamp(18px,3vw,28px) clamp(18px,4vw,36px)" }}>

                {/* Header badge */}
                <div style={{
                  display:"inline-flex", alignItems:"center", gap:8, marginBottom:12,
                  background:"rgba(255,255,255,0.08)", borderRadius:20, padding:"4px 14px",
                  border:"1px solid rgba(255,255,255,0.15)",
                }}>
                  <span style={{ fontSize:13 }}>🍃</span>
                  <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.14em",
                    color:"rgba(255,255,255,0.7)", textTransform:"uppercase",
                    fontFamily:"'Inter',sans-serif" }}>
                    {seasonalRegimen?.srBadge || "Seasonal Wisdom"}
                  </span>
                </div>

                <h2 style={{ fontFamily:"'Playfair Display',serif",
                  fontSize:"clamp(20px,2.6vw,28px)", fontWeight:800,
                  color:"#fff", lineHeight:1.2, margin:"0 0 6px" }}>
                  {seasonalRegimen?.srHeading1 || "Seasonal Regimen"}
                </h2>
                <p style={{ margin:"0 0 18px", fontSize:13, maxWidth:680,
                  color:"rgba(255,255,255,0.55)", fontFamily:"'Merriweather',serif",
                  fontStyle:"italic", lineHeight:1.6 }}>
                  {seasonalRegimen?.srSubtitle || "Simple Ayurvedic guidance for the general public — live in harmony with nature's rhythms."}
                </p>

                {/* ── Bento grid: Do's + Avoid (large) · Tip (full-width) · 4 quick-fact tiles (small) ── */}
                <div className="sr-bento-grid" style={{
                  display:"grid",
                  gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",
                  gap:10,
                  alignItems:"start",
                }}>

                  {/* DO'S — large bento tile */}
                  <div style={{
                    gridColumn:"span 2",
                    background:"rgba(74,124,89,0.18)", borderRadius:16, padding:"14px 16px",
                    border:"1px solid rgba(74,124,89,0.35)", backdropFilter:"blur(4px)",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                      <span style={{ fontSize:15 }}>✅</span>
                      <span style={{ fontSize:11, fontWeight:800, letterSpacing:"0.1em",
                        color:"rgba(144,238,144,0.9)", textTransform:"uppercase",
                        fontFamily:"'Inter',sans-serif" }}>Do's</span>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                      {(seasonalRegimen?.srDos || "Eat light warm meals|Wake at sunrise|Use bitter herbs|Light yoga daily|Drink ginger water")
                        .split(/[|\n]/).map(s => s.trim()).filter(Boolean).map((item, i) => (
                        <div key={i} style={{ display:"flex", gap:7, alignItems:"flex-start" }}>
                          <div style={{ width:5, height:5, borderRadius:"50%", flexShrink:0,
                            background:"rgba(144,238,144,0.7)", marginTop:5 }}/>
                          <span style={{ fontSize:11.5, color:"rgba(255,255,255,0.78)",
                            fontFamily:"'Merriweather',serif", lineHeight:1.45 }}>
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AVOID — large bento tile */}
                  <div style={{
                    gridColumn:"span 2",
                    background:"rgba(184,92,56,0.15)", borderRadius:16, padding:"14px 16px",
                    border:"1px solid rgba(184,92,56,0.32)", backdropFilter:"blur(4px)",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                      <span style={{ fontSize:15 }}>🚫</span>
                      <span style={{ fontSize:11, fontWeight:800, letterSpacing:"0.1em",
                        color:"rgba(255,160,122,0.9)", textTransform:"uppercase",
                        fontFamily:"'Inter',sans-serif" }}>Avoid</span>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                      {(seasonalRegimen?.srAvoids || "Heavy oily foods|Sleeping during day|Cold drinks|Excess sour intake|Skipping meals")
                        .split(/[|\n]/).map(s => s.trim()).filter(Boolean).map((item, i) => (
                        <div key={i} style={{ display:"flex", gap:7, alignItems:"flex-start" }}>
                          <div style={{ width:5, height:5, borderRadius:"50%", flexShrink:0,
                            background:"rgba(255,160,122,0.7)", marginTop:5 }}/>
                          <span style={{ fontSize:11.5, color:"rgba(255,255,255,0.78)",
                            fontFamily:"'Merriweather',serif", lineHeight:1.45 }}>
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* VAIDYA'S TIP — full-width bento banner */}
                  <div style={{
                    gridColumn:"1 / -1",
                    background:"rgba(247,201,72,0.1)", borderRadius:14,
                    padding:"12px 16px",
                    border:"1px solid rgba(247,201,72,0.25)",
                    display:"flex", gap:12, alignItems:"flex-start",
                  }}>
                    <span style={{ fontSize:18, flexShrink:0, marginTop:1 }}>💡</span>
                    <div>
                      <p style={{ margin:"0 0 2px", fontSize:10, fontWeight:700,
                        color:C.turmeric, fontFamily:"'Inter',sans-serif",
                        textTransform:"uppercase", letterSpacing:"0.1em" }}>
                        Vaidya's Tip
                      </p>
                      <p style={{ margin:0, fontSize:11.5, color:"rgba(255,255,255,0.72)",
                        fontFamily:"'Merriweather',serif", lineHeight:1.5, fontStyle:"italic" }}>
                        {seasonalRegimen?.srVaidyaTip || "This season, focus on clearing accumulated Kapha. Start your day with a cup of warm honey-ginger water and a 15-minute walk outdoors."}
                      </p>
                    </div>
                  </div>

                  {/* 4 small bento quick-fact tiles */}
                  {[
                    { icon:seasonalRegimen?.srCard1Icon||"🌅", title:seasonalRegimen?.srCard1Title||"Best Wake Time",  desc:seasonalRegimen?.srCard1Desc||"5:30 – 6:00 AM"    },
                    { icon:seasonalRegimen?.srCard2Icon||"🌾", title:seasonalRegimen?.srCard2Title||"Ideal Foods",     desc:seasonalRegimen?.srCard2Desc||"Barley, mung, honey" },
                    { icon:seasonalRegimen?.srCard3Icon||"🧘", title:seasonalRegimen?.srCard3Title||"Mind Practice",   desc:seasonalRegimen?.srCard3Desc||"Meditation 10 min"   },
                    { icon:seasonalRegimen?.srCard4Icon||"🌿", title:seasonalRegimen?.srCard4Title||"Key Herb",        desc:seasonalRegimen?.srCard4Desc||"Triphala at bedtime" },
                  ].map((card, i) => (
                    <motion.div key={i}
                      initial={{ opacity:0, y:12 }} whileInView={{ opacity:1, y:0 }}
                      viewport={{ once:true }} transition={{ delay: i*0.07 }}
                      style={{
                        gridColumn:"span 1",
                        background:"rgba(255,255,255,0.06)",
                        border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:14, padding:"10px 12px",
                        display:"flex", flexDirection:"column", gap:5,
                        backdropFilter:"blur(4px)",
                      }}>
                      <div style={{ fontSize:19 }}>{card.icon}</div>
                      <div>
                        <p style={{ margin:"0 0 2px", fontSize:9, fontWeight:700,
                          color:"rgba(255,255,255,0.5)", fontFamily:"'Inter',sans-serif",
                          textTransform:"uppercase", letterSpacing:"0.09em" }}>
                          {card.title}
                        </p>
                        <p style={{ margin:0, fontSize:12, color:"rgba(255,255,255,0.85)",
                          fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
                          {card.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      )}

      {/* ════════════════════════════════════════
          §2  ABOUT STRIP
          ════════════════════════════════════════ */}
      <section style={{ padding:"clamp(52px,8vw,88px) 20px", background:"#fff" }}>
        <div style={{ maxWidth:1100, margin:"0 auto",
          display:"grid",
          gridTemplateColumns:"repeat(auto-fit,minmax(min(340px,100%),1fr))",
          gap:"clamp(32px,5vw,64px)", alignItems:"center" }}>

          {/* Illustration side */}
          <motion.div initial={{ opacity:0, x:-30 }}
            whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}
            transition={{ duration:0.6 }}
            style={{ display:"flex", justifyContent:"center",
              position:"relative" }}>
            <div style={{ width:"clamp(220px,45vw,350px)", height:"clamp(220px,45vw,350px)", borderRadius:"50%",
              background:`radial-gradient(circle,${C.saffronL},${C.creamD})`,
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:`0 22px 64px rgba(232,146,42,0.13)` }}>
              <HerbsBowl size={230}/>
            </div>
            {/* Orbiting ring */}
            <motion.div animate={{ rotate:360 }}
              transition={{ duration:22, repeat:Infinity, ease:"linear" }}
              style={{ position:"absolute", width:"clamp(220px,45vw,350px)", height:"clamp(220px,45vw,350px)",
                top:"50%", left:"50%",
                transform:"translate(-50%,-50%)", pointerEvents:"none" }}>
              <div style={{ position:"absolute", top:0, left:"50%",
                transform:"translateX(-50%)", opacity:0.45 }}>
                <Leaf size={34} color={C.sage}/>
              </div>
              <div style={{ position:"absolute", bottom:0, left:"50%",
                transform:"translateX(-50%)", opacity:0.35 }}>
                <Leaf size={28} color={C.sage} rot={180}/>
              </div>
            </motion.div>
            {/* Floating lotus badge */}
            <motion.div animate={{ y:[-7,7,-7] }}
              transition={{ duration:3.2, repeat:Infinity }}
              style={{ position:"absolute", bottom:18, right:16,
                background:"#fff", borderRadius:18, padding:"10px 14px",
                boxShadow:"0 8px 24px rgba(0,0,0,0.10)",
                border:`1px solid ${C.saffronL}`,
                display:"flex", alignItems:"center", gap:8 }}>
              <Lotus size={32} color={C.saffron}/>
              <div>
                <div style={{ fontSize:11, color:C.inkLight,
                  fontFamily:"'Inter',sans-serif" }}>Established</div>
                <div style={{ fontSize:15, fontWeight:700, color:C.ink,
                  fontFamily:"'Playfair Display',serif" }}>
                  {cms.established || "2012"}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Text side */}
          <motion.div initial={{ opacity:0, x:30 }}
            whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}
            transition={{ duration:0.6, delay:0.15 }}>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em",
              color:C.saffronD, textTransform:"uppercase",
              fontFamily:"'Inter',sans-serif", marginBottom:10 }}>
              About Our Clinic
            </p>
            <h2 style={{ fontFamily:"'Playfair Display',serif",
              fontSize:"clamp(24px,3.5vw,40px)", fontWeight:700,
              color:C.ink, lineHeight:1.25, marginBottom:18 }}>
              Healing Rooted in<br/>
              <span style={{ color:C.saffronD }}>Classical Wisdom</span>
            </h2>
            <p style={{ fontSize:15, color:C.inkMid, lineHeight:1.85,
              marginBottom:28, fontFamily:"'Merriweather',serif" }}>
              {aboutSummary}
            </p>
            <div style={{ display:"flex", flexDirection:"column",
              gap:11, marginBottom:32 }}>
              {[
                "Classical nadi pariksha and pulse diagnosis",
                "Authentic Ashtanga Hridayam protocols",
                "Zero synthetic additives in all formulations",
              ].map((pt,i) => (
                <div key={i} style={{ display:"flex", gap:12,
                  alignItems:"flex-start" }}>
                  <div style={{ width:22, height:22, borderRadius:"50%", flexShrink:0,
                    background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    marginTop:1 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10">
                      <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white"
                        strokeWidth="1.6" strokeLinecap="round" fill="none"/>
                    </svg>
                  </div>
                  <span style={{ fontSize:14, color:C.inkMid,
                    fontFamily:"'Merriweather',serif" }}>{pt}</span>
                </div>
              ))}
            </div>
            <Btn to="/about">Learn More →</Btn>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          §3  STATS  — animated counters
          ════════════════════════════════════════ */}
      <section style={{
        background:`linear-gradient(135deg,${C.sageD} 0%,${C.sage} 60%,${C.sageD} 100%)`,
        padding:"64px 24px", position:"relative", overflow:"hidden" }}>
        {/* Subtle mandala watermark */}
        <div style={{ position:"absolute", right:-60, top:-60, opacity:0.04 }}>
          <Lotus size={280} color="#fff"/>
        </div>
        <div style={{ maxWidth:960, margin:"0 auto",
          display:"grid",
          gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",
          gap:32, textAlign:"center", position:"relative" }}>
          {stats.map((s,i) => (
            <motion.div key={i}
              initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
              viewport={{ once:true }} transition={{ delay:i*0.1 }}>
              <div style={{ fontFamily:"'Playfair Display',serif",
                fontSize:"clamp(40px,5.5vw,60px)", fontWeight:800,
                color:C.turmeric, lineHeight:1 }}>
                <CountUp to={s.n} suffix={s.s}/>
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.72)",
                marginTop:8, fontFamily:"'Merriweather',serif" }}>
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
          §4  TREATMENTS  — horizontal scroll
          ════════════════════════════════════════ */}
      <section style={{ padding:"clamp(48px,7vw,84px) 20px", background:C.cream }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <Head tag="Our Specialities" title="Treatments We Offer"
            sub="Time-tested Ayurvedic therapies, each adapted to your constitution and current state of health."/>
          <TreatmentScroll items={treatments}/>
          <div style={{ textAlign:"center", marginTop:36 }}>
            <Btn to="/services">See All Treatments →</Btn>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          §5  TRUST BAND
          ════════════════════════════════════════ */}
      <section style={{
        background:`linear-gradient(135deg,${C.saffronL},${C.creamD})`,
        padding:"58px 24px",
        borderTop:`3px solid ${C.saffron}20`,
        borderBottom:`3px solid ${C.saffron}20` }}>
        <motion.div initial={{ opacity:0 }} whileInView={{ opacity:1 }}
          viewport={{ once:true }}
          style={{ maxWidth:780, margin:"0 auto", textAlign:"center" }}>
          <Lotus size={52} color={C.saffron}/>
          <p style={{ fontSize:"clamp(16px,2.4vw,22px)", color:C.inkMid,
            lineHeight:1.85, marginTop:16,
            fontFamily:"'Merriweather',serif", fontStyle:"italic" }}>
            {trustText}
          </p>
          {phoneNo && (
            <div style={{ marginTop:24, display:"flex", gap:12,
              justifyContent:"center", flexWrap:"wrap" }}>
              <Btn href={`tel:${phoneNo}`}>
                📞 <a href={`tel:${phoneNo}`} style={{ color:"inherit", textDecoration:"none" }}>Call Us: {phoneNo}</a>
              </Btn>
            </div>
          )}
        </motion.div>
      </section>

      {/* ════════════════════════════════════════
          §5b  ONLINE CONSULTATION CARD
          ════════════════════════════════════════ */}
      <section style={{ padding:"clamp(44px,7vw,80px) 20px", background:C.cream }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <Head
            tag={cms.ocSectionTag   || "Consult From Home"}
            title={cms.ocSectionTitle || "Online Consultation"}
            sub={cms.ocSectionSub   || "Book a video or phone consultation with our doctors — from anywhere in the world."}/>
          <motion.div
            initial={{ opacity:0, y:28 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} transition={{ duration:0.55 }}
            style={{
              background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
              borderRadius:28, overflow:"hidden", position:"relative",
              boxShadow:"0 24px 64px rgba(0,0,0,0.22)",
            }}>
            {/* Background decoration */}
            <div style={{ position:"absolute", right:-60, top:-60, opacity:0.06 }}>
              <Lotus size={320} color="#fff"/>
            </div>
            <div style={{ position:"absolute", left:-30, bottom:-30, opacity:0.04 }}>
              <Lotus size={200} color={C.turmeric}/>
            </div>

            <div style={{
              display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(min(320px,100%),1fr))",
              gap:0, position:"relative", zIndex:1,
            }}>
              {/* Left: content */}
              <div style={{ padding:"clamp(28px,5vw,52px) clamp(20px,5vw,48px)" }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:8, marginBottom:20,
                  background:"rgba(255,255,255,0.08)", borderRadius:20, padding:"5px 16px",
                  border:"1px solid rgba(255,255,255,0.15)" }}>
                  <span style={{ fontSize:14 }}>💻</span>
                  <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em",
                    color:"rgba(255,255,255,0.7)", textTransform:"uppercase",
                    fontFamily:"'Inter',sans-serif" }}>{cms.ocBadgeLabel || "Available Worldwide"}</span>
                </div>

                <h2 style={{ fontFamily:"'Playfair Display',serif",
                  fontSize:"clamp(26px,3.5vw,40px)", fontWeight:800,
                  color:"#fff", lineHeight:1.2, margin:"0 0 16px" }}>
                  {cms.ocHeading1 || "Ayurvedic Care,"}<br/>
                  <span style={{ color:C.turmeric }}>{cms.ocHeading2 || "Wherever You Are"}</span>
                </h2>

                <p style={{ fontSize:15, color:"rgba(255,255,255,0.68)", lineHeight:1.8,
                  fontFamily:"'Merriweather',serif", margin:"0 0 32px",
                  maxWidth:380 }}>
                  {cms.ocDescription || "Connect with our experienced doctors via video call or phone. Get personalised Ayurvedic guidance, herbal prescriptions, and diet plans — all from the comfort of your home."}
                </p>

                {/* Feature list */}
                <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:36 }}>
                  {[
                    { icon:cms.ocFeat1Icon||"📹", text:cms.ocFeat1Text||"HD Video or Phone consultation" },
                    { icon:cms.ocFeat2Icon||"🌍", text:cms.ocFeat2Text||"Works with any timezone worldwide" },
                    { icon:cms.ocFeat3Icon||"💳", text:cms.ocFeat3Text||"Secure payment via Razorpay" },
                    { icon:cms.ocFeat4Icon||"📋", text:cms.ocFeat4Text||"Digital prescription delivered instantly" },
                  ].map((f,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:34, height:34, borderRadius:10, flexShrink:0,
                        background:"rgba(255,255,255,0.08)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:16, border:"1px solid rgba(255,255,255,0.12)" }}>
                        {f.icon}
                      </div>
                      <span style={{ fontSize:13, color:"rgba(255,255,255,0.72)",
                        fontFamily:"'Inter',sans-serif" }}>{f.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <motion.a href="/request"
                  whileHover={{ scale:1.04, boxShadow:`0 14px 36px rgba(247,201,72,0.45)` }}
                  whileTap={{ scale:0.97 }}
                  style={{
                    display:"inline-flex", alignItems:"center", gap:10,
                    padding:"14px 32px", borderRadius:50, textDecoration:"none",
                    background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`,
                    color:C.ink, fontWeight:700, fontSize:15,
                    fontFamily:"'Inter',sans-serif",
                    boxShadow:`0 8px 28px rgba(247,201,72,0.35)`,
                    transition:"all 0.25s",
                  }}>
                  🌿 {cms.ocCtaText || "Book Online Consultation"}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke={C.ink} strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.a>
              </div>

              {/* Right: feature cards */}
              <div style={{ padding:"clamp(20px,4vw,52px) clamp(20px,4vw,40px) clamp(20px,4vw,52px) clamp(16px,3vw,20px)",
                display:"flex", flexDirection:"column", justifyContent:"center", gap:16 }}>
                {[
                  { icon:cms.ocCard1Icon||"🕐", title:cms.ocCard1Title||"24-hr Confirmation",   desc:cms.ocCard1Desc||"We confirm your slot within 24 hours of booking." },
                  { icon:cms.ocCard2Icon||"🔒", title:cms.ocCard2Title||"Confidential & Secure", desc:cms.ocCard2Desc||"All consultations and records are fully private." },
                  { icon:cms.ocCard3Icon||"🌿", title:cms.ocCard3Title||"Classical Ayurveda",    desc:cms.ocCard3Desc||"Same authentic protocols as our in-person clinic." },
                  { icon:cms.ocCard4Icon||"📱", title:cms.ocCard4Title||"Any Device",            desc:cms.ocCard4Desc||"Join from phone, tablet, or computer — no app needed." },
                ].map((card, i) => (
                  <motion.div key={i}
                    initial={{ opacity:0, x:20 }} whileInView={{ opacity:1, x:0 }}
                    viewport={{ once:true }} transition={{ delay: i*0.08 }}
                    style={{
                      background:"rgba(255,255,255,0.06)",
                      border:"1px solid rgba(255,255,255,0.1)",
                      borderRadius:16, padding:"16px 18px",
                      display:"flex", gap:14, alignItems:"flex-start",
                      backdropFilter:"blur(4px)",
                    }}>
                    <div style={{ fontSize:24, flexShrink:0, marginTop:2 }}>{card.icon}</div>
                    <div>
                      <p style={{ margin:"0 0 3px", fontSize:13, fontWeight:700,
                        color:"rgba(255,255,255,0.9)", fontFamily:"'Inter',sans-serif" }}>
                        {card.title}
                      </p>
                      <p style={{ margin:0, fontSize:12, color:"rgba(255,255,255,0.5)",
                        fontFamily:"'Merriweather',serif", lineHeight:1.55 }}>
                        {card.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          §6  TESTIMONIALS
          ════════════════════════════════════════ */}
      <section style={{ padding:"clamp(48px,7vw,84px) 20px", background:"#fff" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <Head
            tag={cms.testiSectionTag   || "Patient Stories"}
            title={cms.testiSectionTitle || "Words from Our Patients"}
            sub={cms.testiSectionSub   || "Real experiences from people who found balance and healing at our clinic."}/>
          <Testimonials list={testimonials}/>
        </div>
      </section>

      {/* ════════════════════════════════════════
          §7  STATUS CHECKER
          ════════════════════════════════════════ */}
      <section style={{ padding:"clamp(48px,7vw,84px) 20px", background:C.cream }}>
        <div style={{ maxWidth:540, margin:"0 auto" }}>
          <Head tag="Track Your Visit" title="Check Appointment Status"/>
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            style={{ background:"#fff", borderRadius:24, padding:36,
              boxShadow:"0 8px 40px rgba(0,0,0,0.075)",
              border:`1px solid ${C.creamD}` }}>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:220 }}>
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  placeholder="Your phone number"
                />
              </div>
              <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}
                onClick={checkStatus} disabled={loading}
                style={{ padding:"10px 20px", borderRadius:12, border:"none",
                  background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                  color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer",
                  whiteSpace:"nowrap", fontFamily:"'Inter',sans-serif",
                  boxShadow:`0 4px 16px rgba(232,146,42,0.38)`,
                  opacity: loading ? 0.7 : 1 }}>
                {loading ? "…" : "Check"}
              </motion.button>
            </div>

            <AnimatePresence>
              {searched && !loading && results.length === 0 && (
                <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }}
                  style={{ textAlign:"center", color:C.inkLight,
                    marginTop:16, fontSize:14,
                    fontFamily:"'Inter',sans-serif" }}>
                  No records found for this number.
                </motion.p>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {results.map(r => (
                <motion.div key={r._id}
                  initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                  style={{ marginTop:14, border:`1px solid ${C.creamD}`,
                    borderRadius:14, padding:"14px 18px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:6 }}>
                    <span style={{ fontWeight:700, color:C.ink,
                      fontFamily:"'Playfair Display',serif" }}>{r.type}</span>
                    <span style={{ padding:"3px 12px", borderRadius:20,
                      fontSize:11, fontWeight:600,
                      background: r.status==="Pending" ? "#FEF3C7"
                        : r.status==="Replied" ? "#DBEAFE" : C.sageL,
                      color: r.status==="Pending" ? "#92400E"
                        : r.status==="Replied" ? "#1E40AF" : C.sageD }}>
                      {r.status}
                    </span>
                  </div>
                  {r.adminReply && (
                    <div style={{ background:C.sageL, borderRadius:8,
                      padding:"8px 12px", fontSize:13, color:C.inkMid }}>
                      <b>Clinic reply:</b> {r.adminReply}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
          <div style={{ textAlign:"center", marginTop:22 }}>
            <Btn to="/request" outline>📅 Book New Appointment</Btn>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          §8a  CONTACT RIBBON
          ════════════════════════════════════════ */}
      <section style={{
        background:`linear-gradient(100deg,${C.cream} 0%,${C.creamD} 100%)`,
        borderTop:`2px solid ${C.saffronL}`,
        borderBottom:`2px solid ${C.saffronL}`,
        padding:"0 24px",
        position:"relative", overflow:"hidden",
      }}>
        {/* decorative petals */}
        <div style={{ position:"absolute", right:-30, top:"50%", transform:"translateY(-50%)", opacity:0.06, pointerEvents:"none" }}>
          <Lotus size={180} color={C.saffron}/>
        </div>
        <div style={{ position:"absolute", left:-20, top:"50%", transform:"translateY(-50%)", opacity:0.04, pointerEvents:"none" }}>
          <Lotus size={120} color={C.sage}/>
        </div>

        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          {/* Top label */}
          <motion.div initial={{ opacity:0, y:12 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            style={{ textAlign:"center", padding:"28px 0 20px", borderBottom:`1px solid ${C.saffronL}` }}>
            <div style={{
              display:"inline-flex", alignItems:"center", gap:8,
              background:C.saffronL, borderRadius:20, padding:"5px 18px",
              border:`1px solid ${C.saffron}35`,
            }}>
              <span style={{ fontSize:14 }}>📍</span>
              <span style={{ fontSize:11, fontWeight:800, letterSpacing:"0.13em",
                color:C.saffronD, textTransform:"uppercase", fontFamily:"'Inter',sans-serif" }}>
                Get In Touch
              </span>
            </div>
            <h2 style={{ fontFamily:"'Playfair Display',serif",
              fontSize:"clamp(20px,3vw,32px)", fontWeight:800,
              color:C.ink, margin:"10px 0 0" }}>
              We're Here for You
            </h2>
            {clinicMode === "online_only" && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:8,
                background:C.sageL, borderRadius:20, padding:"4px 14px",
                border:`1px solid ${C.sage}40` }}>
                <span style={{ fontSize:12 }}>💻</span>
                <span style={{ fontSize:11, fontWeight:700, color:C.sageD,
                  fontFamily:"'Inter',sans-serif" }}>Online Consultations Only</span>
              </div>
            )}
          </motion.div>

          {/* Contact items row */}
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(auto-fit, minmax(180px,1fr))",
            gap:0,
          }}>
            {[
              {
                icon:"📞", label:"Call Us",
                value: cms.phone || phoneNo || "Contact us",
                href: phoneNo ? `tel:${phoneNo}` : null,
                color: C.sage, bg: C.sageL,
              },
              {
                icon:"💬", label:"WhatsApp",
                value: cms.whatsapp || whatsappNo || "Chat with us",
                href: whatsappNo ? `https://wa.me/${(cms.whatsapp||whatsappNo).replace(/\D/g,"")}` : null,
                color:"#25D366", bg:"#F0FDF4",
                target:"_blank",
              },
              {
                icon:"✉️",  label:"Email",
                value: cms.email || "info@clinic.com",
                href: cms.email ? `mailto:${cms.email}` : null,
                color: C.saffronD, bg: C.saffronL,
              },
              // Address & Hours only shown in physical clinic mode
              ...(clinicMode === "physical" ? [
                {
                  icon:"📍", label:"Address",
                  value: cms.address || "Visit our clinic",
                  href: cms.address
                    ? `https://maps.google.com/?q=${encodeURIComponent(cms.address)}`
                    : null,
                  color: C.terra, bg:"#FFF5F0",
                  target:"_blank",
                },
                {
                  icon:"🕐", label:"Clinic Hours",
                  value: cms.timing || "Mon – Sat, 9 AM – 7 PM",
                  href: null,
                  color: C.inkMid, bg:"#F9FAFB",
                },
              ] : []),
            ].map((item, i, arr) => (
              <motion.div key={i}
                initial={{ opacity:0, y:16 }} whileInView={{ opacity:1, y:0 }}
                viewport={{ once:true }} transition={{ delay: i * 0.08 }}
                whileHover={{ background: item.bg, scale:1.01 }}
                style={{
                  padding:"22px 20px", display:"flex", flexDirection:"column",
                  alignItems:"center", textAlign:"center", gap:8,
                  borderRight: i < arr.length - 1 ? `1px solid ${C.saffronL}` : "none",
                  cursor: item.href ? "pointer" : "default",
                  transition:"background 0.2s",
                  textDecoration:"none",
                  position:"relative", zIndex:1,
                }}
                onClick={() => item.href && window.open(item.href, item.target || "_self")}
              >
                <div style={{
                  width:44, height:44, borderRadius:"50%",
                  background: item.bg,
                  border:`1.5px solid ${item.color}30`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:20,
                }}>
                  {item.icon}
                </div>
                <div>
                  <p style={{ fontSize:10, fontWeight:800, textTransform:"uppercase",
                    letterSpacing:"0.11em", color:item.color,
                    fontFamily:"'Inter',sans-serif", margin:"0 0 3px" }}>
                    {item.label}
                  </p>
                  <p style={{ fontSize:13, color:C.inkMid, fontWeight:600,
                    fontFamily:"'Inter',sans-serif", margin:0, lineHeight:1.45 }}>
                    {item.value}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom action row */}
          <motion.div initial={{ opacity:0, y:12 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            style={{
              padding:"20px 0 28px", borderTop:`1px solid ${C.saffronL}`,
              display:"flex", justifyContent:"center", gap:14, flexWrap:"wrap",
            }}>
            <motion.a href="/request"
              whileHover={{ scale:1.04, boxShadow:`0 12px 30px rgba(232,146,42,0.4)` }}
              whileTap={{ scale:0.97 }}
              style={{
                display:"inline-flex", alignItems:"center", gap:7,
                padding:"12px 28px", borderRadius:50, fontSize:14, fontWeight:700,
                textDecoration:"none", fontFamily:"'Inter',sans-serif",
                background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                color:"#fff", boxShadow:`0 6px 22px rgba(232,146,42,0.32)`,
              }}>
              📅 Book Appointment
            </motion.a>
            {whatsappNo && (
              <motion.a
                href={`https://wa.me/${whatsappNo.replace(/\D/g,"")}`}
                target="_blank" rel="noopener noreferrer"
                whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
                style={{
                  display:"inline-flex", alignItems:"center", gap:7,
                  padding:"12px 28px", borderRadius:50, fontSize:14, fontWeight:700,
                  textDecoration:"none", fontFamily:"'Inter',sans-serif",
                  background:"#25D366", color:"#fff",
                  boxShadow:"0 6px 22px rgba(37,211,102,0.3)",
                }}>
                💬 WhatsApp Us
              </motion.a>
            )}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          §8  FOOTER CTA BAND
          ════════════════════════════════════════ */}
      <section style={{
        background:`linear-gradient(135deg,${C.sageD},${C.sage})`,
        padding:"68px 24px", textAlign:"center",
        position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", left:-40, bottom:-40, opacity:0.05 }}>
          <Lotus size={260} color="#fff"/>
        </div>
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
          viewport={{ once:true }} style={{ position:"relative" }}>
          <Lotus size={52} color={C.turmeric}/>
          <h2 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(24px,4vw,44px)", fontWeight:700,
            color:"#fff", margin:"16px 0 12px" }}>
            Begin Your Healing Journey
          </h2>
          <p style={{ fontSize:16, color:"rgba(255,255,255,0.72)",
            marginBottom:34, fontFamily:"'Merriweather',serif" }}>
            Schedule a consultation and take the first step towards holistic wellness.
          </p>
          <div style={{ display:"flex", gap:14, justifyContent:"center",
            flexWrap:"wrap" }}>
            <Btn to="/request" sx={{
              background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`,
              color:C.ink }}>
              🌿 Book Consultation
            </Btn>
            {phoneNo && (
              <Btn href={`tel:${phoneNo}`} sx={{
                background:"transparent",
                border:"2px solid rgba(255,255,255,0.45)",
                color:"#fff", boxShadow:"none" }}>
                📞 {phoneNo}
              </Btn>
            )}
          </div>
        </motion.div>
      </section>


      {/* ── Responsive styles for Daily Posts ── */}
      <style>{`
        @media (max-width: 480px) {
          /* daily post image height on small phones */
          .dp-img { height: 200px !important; }
        }
        /* Seasonal Regimen bento grid: force 2 columns on mobile so the
           4 quick-fact tiles sit 2x2 instead of stacking one per row,
           which was making the whole card scroll like a long screenshot */
        @media (max-width: 640px) {
          .sr-bento-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
        }
      `}</style>
      <WAFloat number={whatsappNo}/>
    </div>
  );
}